import React, { useEffect } from 'react';
import { Tooltip } from 'antd';
import FilterDropdown from '../../FilterDropdown/FilterDropdown';
import FilterButton from '../../FilterButton/FilterButton';
import useFilterState from '../../../hooks/useFilterState';
import useFilterItemsWithAmount from '../../../hooks/useFilterItemsWithAmount';
import { filtersData } from '../../../constants/filtersData';
import { filterNames } from '../../../constants/filterNames';
import FilterContainer from '../../FilterContainer/FilterContainer';
import SelectedOption from '../../SelectedOption/SelectedOption';
import { useFilterOptionsShorter } from '../../../hooks/useFilterOptionsShorter';
import { HiddenSelectedOptions } from '../../HiddenSelectedOptions/HiddenSelectedOptions';
import useFlatsArray from '../../../queries/useFlatsArray';

const housesFilter = filtersData[filterNames.houses];
const filter = filtersData[filterNames.flats];

const ignoredRoomTypes = new Set([1, 2]);

const generateText = (item) => {
  const { typeId, typeShortName, flat, name } = item;
  const type = !ignoredRoomTypes.has(typeId) ? typeShortName : '';
  const roomName = name || flat;

  return [type, roomName].filter(Boolean).join(' ');
};

const FlatsFilter = ({ disabled = false }) => {
  const [housesFilterState] = useFilterState(housesFilter);
  const [filterState, setFilterState] = useFilterState(filter);

  const { data, isFetching } = useFlatsArray(housesFilterState, {
    enabled: !!housesFilterState.length,
    staleTime: Infinity,
  });

  const { items, itemsMap } = useFilterItemsWithAmount({
    data,
    requestKey: 'addressId',
  });

  const handleItemRemove = (id) => {
    const newState = filterState.filter((itemId) => itemId !== id);

    setFilterState(newState);
  };

  const hasFilter = filterState.length > 0;

  const allSelected = hasFilter && itemsMap.size === filterState.length;

  useEffect(() => {
    if (housesFilterState.length === 0) {
      setFilterState([]);
    }
  }, [housesFilterState]);

  useEffect(() => {
    if (items.length) {
      const setFilter = (prev) =>
        prev.filter((filterId) => items.some((item) => item.id === filterId));

      setFilterState(setFilter);
    }
  }, [items]);

  const filteredItems = items.filter((item) => filterState.includes(item.id));
  const { visibleItems, hiddenItems } = useFilterOptionsShorter(filteredItems);

  return (
    <FilterContainer>
      <Tooltip title={housesFilterState.length === 0 ? 'Выберите дом' : ''}>
        <div>
          <FilterDropdown
            disabled={isFetching || housesFilterState.length === 0 || disabled}
            filter={filter}
            items={items}
            getItemContent={(item) => generateText(item)}
            getSearchContent={(item) => generateText(item)}
            renderButton={({ visible }) => (
              <FilterButton
                text="Помещения"
                opened={visible}
                selected={hasFilter}
              />
            )}
          />
        </div>
      </Tooltip>
      {allSelected ? (
        <SelectedOption
          text="Все"
          onRemove={() => setFilterState([])}
        />
      ) : (
        <>
          {visibleItems.map((item) => (
            <SelectedOption
              key={item.id}
              text={item.name}
              value={item.id}
              onRemove={handleItemRemove}
            />
          ))}
          {Array.isArray(hiddenItems) && !!hiddenItems.length && (
            <HiddenSelectedOptions
              hiddenItems={hiddenItems}
              handleItemRemove={handleItemRemove}
            />
          )}
        </>
      )}
    </FilterContainer>
  );
};

export default FlatsFilter;
